import {
    materialLevelsColors,
    materialLevelsIcons,
    materialLevelsNames,
} from "@common/chapters/ActivityLevelBadge"
import { MATERIAL_DURATION_OPTIONS } from "@common/chapters/materialDuration"
import ChapterItemComponent from "@common/chapters/ChapterItemComponent"
import ChapterSearchDropdown, { BLUR_CLOSE_DELAY_MS } from "@common/chapters/ChapterSearchDropdown"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { IconImageM } from "@common/components/icons/IconsM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { FontAwesome5 } from "@expo/vector-icons"
import { LinearGradient } from "expo-linear-gradient"
import React, { useContext, useEffect, useRef, useState } from "react"
import { Controller } from "react-hook-form"
import { TouchableOpacity, View } from "react-native"
import { TextInput, Tooltip, Switch } from "react-native-paper"
import { SelectInputM } from "../../../student/account/onboarding/SelectInputM"
import SearchableSelectM from "../../../common/components/input/SearchableSelectM"
import TextInputM from "../../../common/components/input/TextInputM"
import UncontrolledTextInputM from "../../../common/components/input/UncontrolledTextInputM"

const TYPEWRITER_STEPS = 6
const TYPEWRITER_STEP_MS = 70

/**
 * Material generation form with class/subject/chapter/level/instructions
 */
const MaterialGenerationForm = ({
    control,
    // Le formulaire est aussi monté depuis l'espace parent (bouton « + » de la
    // todo-list) : seule la voix des textes change.
    isParentMode = false,
    classOptions,
    subjectOptions,
    levelOptions,
    chaptersOptions,
    chapters, // Full chapter objects for ChapterSearchDropdown
    useStudyProgram,
    setUseStudyProgram,
    watchedClass,
    watchedSubject,
    watchedInstructions,
    watchedChapter,
    watchedStudyProgramChapter,
    setShowChapterSelection,
    hasStudyProgram,
    getChaptersRequest,
    isFormValid,
    onSubmit,
    // Personalization props
    studentInterests,
    personalizeWithInterests,
    setPersonalizeWithInterests,
    // Priority chapters props (from upcoming assessment)
    priorityChapterIds = [],
    priorityBadge = null,
    // Optional slot rendered right below the Chapter search field, used by
    // GenerateActivityModal to show the "Compétences travaillées" chip picker.
    skillPartsSection = null,
    // Optional slot rendered between the Chapter dropdown and the
    // skillPartsSection, used by the image flow to surface the AI's
    // chapter suggestion as a dismissable chip without blocking the form.
    chapterSuggestionSlot = null,
    // Cross-class chapter search fallback — fires when the tutor picks a
    // result from the "Voir dans toutes les classes" dropdown so the parent
    // can sync its class + subject state before setting the chapter.
    onGlobalChapterSelect = null,
    currentSubjectName = null,
    currentSubjectId = null,
    // Optional slot rendered just below the primary "Générer" button —
    // used by the image flow to surface the parcours generation actions
    // ("Pour réviser" / "Pour s'entraîner") as outlined secondary buttons.
    secondaryActionsSlot = null,
    // Optional override for the primary action button. When the tutor has
    // uploaded an image, the image-first flow replaces "Générer une activité"
    // with "Créer l'activité" (which uses the uploaded image directly).
    // If null/undefined, the default "Générer une activité" gradient button
    // is rendered (text-only flow).
    primaryActionOverride = null,
    // When true, the form hides the Type and Instructions inputs from the
    // main form — used by the image flow where the parent renders these
    // fields inside an inline "Personnaliser…" expand under the secondary
    // actions instead. Duration and the personalize-with-interests toggle
    // are still rendered in the main form (duration applies to image-direct
    // create; the toggle is only relevant to the parcours expand though).
    hideTypeAndInstructions = false,
    // Image-flow fallback: when the AI analyse fails or omits the level,
    // the parent surfaces the Type selector again so the tutor can still
    // pick a difficulty level for the upcoming activity. Only affects the
    // Type input — Instructions stays hidden under hideTypeAndInstructions
    // since they remain irrelevant for the image-direct create path.
    forceShowType = false,
    // Image-flow inline AI suggestions: when the analysis returns medium
    // confidence, the parent passes the suggestion + alternatives so the
    // ChapterSearchDropdown surfaces them inside its open list (no auto-select,
    // no standalone chip). Picking an item calls the regular onSelectChapter.
    aiSuggestions = null,
    // When true, the form skips rendering its internal Class + Subject row
    // (used by the no-image flow in GenerateActivityModal which lifts that
    // row into a modal-level "scope header" above the form). The image flow
    // keeps the default (false) so the row stays inside the form.
    hideClassSubject = false,
    // react-hook-form's setValue, needed only for the "no chapter found"
    // hand-off below — everything else in this form goes through
    // Controller/watch and never touches it directly.
    setValue = null,
}) => {
    const dimensions = useContext(DimensionsContext)
    const isSmallScreen = dimensions?.width < 800
    const showTypeInput = !hideTypeAndInstructions || forceShowType

    // Carry a dead-end chapter search into Instructions instead of leaving
    // the tutor stuck on "Aucun chapitre trouvé". The focus waits out the
    // dropdown's blur-close delay, otherwise that close steals it back.
    const instructionsInputRef = useRef(null)
    const typewriterTimerRef = useRef(null)
    // Full text while the typewriter is still running, null otherwise — so a
    // submit mid-animation sends the whole search, not a truncated prefix.
    const pendingInstructionsRef = useRef(null)
    const stopTypewriter = () => {
        clearInterval(typewriterTimerRef.current)
        typewriterTimerRef.current = null
        pendingInstructionsRef.current = null
    }
    useEffect(() => stopTypewriter, [])
    const submit = () => {
        if (pendingInstructionsRef.current !== null) {
            setValue?.("instructions", pendingInstructionsRef.current)
            stopTypewriter()
        }
        onSubmit()
    }

    // Instructions becomes the recommended path after a dead-end search: a
    // steady state (primary outline + "Sam créera le chapitre" tag), not a
    // one-off effect the tutor can miss. Picking a chapter switches it off
    // for good, not just while that chapter stays selected.
    const [instructionsRecommended, setInstructionsRecommended] = useState(false)
    useEffect(() => {
        if (watchedChapter) setInstructionsRecommended(false)
    }, [watchedChapter])
    const instructionsHighlighted = instructionsRecommended && !watchedChapter

    const handleChapterSearchNoResults = (searchText) => {
        setInstructionsRecommended(true)
        setTimeout(() => instructionsInputRef.current?.focus(), BLUR_CLOSE_DELAY_MS + 50)

        // The searched text types itself in rather than appearing at once,
        // so the tutor sees it travel from the dead-end search into
        // Instructions. A fixed number of steps, whatever the length: each
        // step re-renders the whole modal (it watches `instructions`), so
        // one step per letter cost ~20 renders for a 20-character search.
        // Never overwrites what was already typed there, and stops the
        // moment the tutor types (see the field's onChangeText).
        if (watchedInstructions?.trim() || !searchText) return
        stopTypewriter()
        pendingInstructionsRef.current = searchText
        const chunk = Math.ceil(searchText.length / TYPEWRITER_STEPS)
        let typed = 0
        typewriterTimerRef.current = setInterval(() => {
            typed = Math.min(typed + chunk, searchText.length)
            setValue?.("instructions", searchText.slice(0, typed))
            if (typed >= searchText.length) stopTypewriter()
        }, TYPEWRITER_STEP_MS)
    }

    return (
        <>
            {/* Study Program Toggle */}
            {hasStudyProgram && (
                <View style={{ marginBottom: 20, flexDirection: "row", alignItems: "center" }}>
                    {/* Reporting the toggle is all this form does with the mode:
                        the owner reacts to it and is the only writer of the
                        scope fields (class / matière / chapter). Resetting them
                        from here too is what let the two disagree in study-
                        program mode. */}
                    <Switch
                        value={useStudyProgram}
                        onValueChange={setUseStudyProgram}
                        color={theme.colors.primary}
                    />
                    <TextM
                        color={theme.colors.primary}
                        fontSize={14}
                        fontWeight="Medium"
                        style={{ marginLeft: 10 }}
                    >
                        Mode programme de révision
                    </TextM>
                </View>
            )}

            {useStudyProgram ? (
                /* Study Program Mode */
                <View style={{ marginBottom: 15 }}>
                    <TextM
                        color={theme.colors.primary}
                        fontWeight="Medium"
                        fontSize={12}
                        style={{ marginBottom: 10 }}
                    >
                        Chapitre du programme
                    </TextM>
                    {watchedStudyProgramChapter ? (
                            <ChapterItemComponent
                                chapter={watchedStudyProgramChapter}
                                withParentChapter={true}
                                onPress={() => setShowChapterSelection(true)}
                                style={{ marginTop: 0 }}
                                width={"100%"}
                                renderChapterOnly
                            />
                    ) : (
                        <TouchableOpacity
                            style={{
                                borderWidth: 1,
                                borderColor: theme.colors.lightMore,
                                borderRadius: 10,
                                paddingVertical: 15,
                                paddingHorizontal: 15,
                                alignItems: "center",
                                backgroundColor: theme.colors.primary,
                                flexDirection: "row",
                                justifyContent: "center"
                            }}
                            onPress={() => setShowChapterSelection(true)}
                        >
                            <FontAwesome5
                                name="plus"
                                size={16}
                                color={theme.colors.white}
                                style={{ marginRight: 10 }}
                            />
                            <TextM color={theme.colors.white} fontWeight="Bold" fontSize={14} wrap>
                                Sélectionner un chapitre
                            </TextM>
                        </TouchableOpacity>
                    )}
                    {/* Analyse feedback (image flow). The chapter is already
                        settled here, so the slot only ever shows the "analyse
                        running / failed" states — but the tutor still needs
                        those: without them a dropped file looks ignored. */}
                    {chapterSuggestionSlot}
                    {/* Skill parts chips (rendered only when the chapter has them) */}
                    {skillPartsSection}
                </View>
            ) : (
                /* Manual Selection Mode */
                <>
                    {/* First row: Class and Subject — skipped when the parent
                        lifts this row into a modal-level scope header. */}
                    {!hideClassSubject && (
                        <View style={{ flexDirection: "row", marginBottom: 15, zIndex: 1100 }}>
                            <View style={{ flex: 1, marginRight: 10 }}>
                                <Controller
                                    control={control}
                                    name="selectedClass"
                                    render={({ field: { onChange, value } }) => (
                                        <SearchableSelectM
                                            label="Classe"
                                            options={classOptions}
                                            value={value}
                                            onSelect={onChange}
                                            placeholder="Rechercher une classe..."
                                        />
                                    )}
                                />
                            </View>
                            <View style={{ flex: 1, marginLeft: 10 }}>
                                <Controller
                                    control={control}
                                    name="selectedSubject"
                                    render={({ field: { onChange, value } }) => (
                                        <SearchableSelectM
                                            label="Matière"
                                            options={subjectOptions}
                                            value={value}
                                            onSelect={onChange}
                                            placeholder="Rechercher une matière..."
                                        />
                                    )}
                                />
                            </View>
                        </View>
                    )}

                    {/* Third row: Chapter */}
                    <View style={{ marginBottom: 15, zIndex: 1000 }}>
                        <Controller
                            control={control}
                            name="selectedChapter"
                            render={({ field: { onChange, value } }) => (
                                <ChapterSearchDropdown
                                    label="Chapitre"
                                    chapters={chapters || []}
                                    selectedChapterId={value}
                                    onSelectChapter={onChange}
                                    loading={getChaptersRequest}
                                    disabled={!watchedClass || !watchedSubject}
                                    placeholder={
                                        !watchedClass || !watchedSubject
                                            ? "Sélectionner une classe et matière d'abord"
                                            : "Rechercher un chapitre..."
                                    }
                                    priorityChapterIds={priorityChapterIds}
                                    priorityBadge={priorityBadge}
                                    onGlobalSelect={onGlobalChapterSelect}
                                    currentClass={watchedClass}
                                    currentSubjectName={currentSubjectName}
                                    currentSubjectId={currentSubjectId}
                                    aiSuggestions={aiSuggestions}
                                    // Not in the image flow: Instructions is hidden there,
                                    // so the hand-off would land in an invisible field.
                                    onNoResultsUseAsInstructions={
                                        setValue && !hideTypeAndInstructions
                                            ? handleChapterSearchNoResults
                                            : null
                                    }
                                />
                            )}
                        />
                        {/* Single hint surfaced once below the chapter dropdown.
                            Text flow: chapter and instructions are
                            interchangeable (it mirrors the backend's own
                            `chapter_id` OR `instructions` rule), so the hint
                            names both — the old copy ("Choisis un chapitre pour
                            continuer") implied the dropdown was mandatory.
                            Image flow: Instructions is hidden, so the chapter is
                            the only way forward unless the catalogue is empty;
                            the hint keeps the old copy there. */}
                        {watchedClass && watchedSubject && (hideTypeAndInstructions
                            ? !watchedChapter && (getChaptersRequest || chapters?.length > 0)
                            : !isFormValid) && (
                                <TextM
                                    fontSize={11}
                                    color={theme.colors.primary}
                                    style={{ marginTop: 4 }}
                                >
                                    {hideTypeAndInstructions
                                        ? "Choisis un chapitre pour continuer"
                                        : "Choisis un chapitre, ou décris l'exercice dans Instructions ci-dessous"}
                                </TextM>
                            )}
                    </View>

                    {/* Chapter AI suggestion chip (image flow only) */}
                    {chapterSuggestionSlot}

                    {/* Skill parts chips (rendered only when the chapter has them) */}
                    {skillPartsSection}

                    {/* Fourth row: Level and Duration — stacked on small screens */}
                    <View
                        style={{
                            flexDirection: isSmallScreen ? "column" : "row",
                            marginBottom: 15,
                        }}
                    >
                        {showTypeInput && (
                            <View
                                style={{
                                    flex: isSmallScreen ? undefined : 1,
                                    width: isSmallScreen ? "100%" : undefined,
                                    marginRight: isSmallScreen ? 0 : 10,
                                    marginBottom: isSmallScreen ? 15 : 0,
                                }}
                            >
                                <Controller
                                    control={control}
                                    name="selectedLevel"
                                    render={({ field: { onChange, value } }) => (
                                        <SelectInputM
                                            label="Type"
                                            options={levelOptions}
                                            value={value}
                                            onSelect={onChange}
                                        />
                                    )}
                                />
                            </View>
                        )}
                        <View
                            style={{
                                flex: isSmallScreen ? undefined : 1,
                                width: isSmallScreen ? "100%" : undefined,
                                marginLeft: isSmallScreen || !showTypeInput ? 0 : 10,
                            }}
                        >
                            <Controller
                                control={control}
                                name="selectedDuration"
                                render={({ field: { onChange, value } }) => (
                                    <SelectInputM
                                        label="Durée"
                                        options={MATERIAL_DURATION_OPTIONS}
                                        value={value}
                                        onSelect={onChange}
                                    />
                                )}
                            />
                        </View>
                    </View>
                </>
            )}

            {/* Level and Duration selectors for study program mode — stacked on small screens */}
            {useStudyProgram && (
                <View
                    style={{
                        flexDirection: isSmallScreen ? "column" : "row",
                        marginBottom: 15,
                    }}
                >
                    {showTypeInput && (
                        <View
                            style={{
                                flex: isSmallScreen ? undefined : 1,
                                width: isSmallScreen ? "100%" : undefined,
                                marginRight: isSmallScreen ? 0 : 10,
                                marginBottom: isSmallScreen ? 15 : 0,
                            }}
                        >
                            <Controller
                                control={control}
                                name="selectedLevel"
                                render={({ field: { onChange, value } }) => (
                                    <SelectInputM
                                        label="Type"
                                        options={levelOptions}
                                        value={value}
                                        onSelect={onChange}
                                    />
                                )}
                            />
                        </View>
                    )}
                    <View
                        style={{
                            flex: isSmallScreen ? undefined : 1,
                            width: isSmallScreen ? "100%" : undefined,
                            marginLeft: isSmallScreen || !showTypeInput ? 0 : 10,
                        }}
                    >
                        <Controller
                            control={control}
                            name="selectedDuration"
                            render={({ field: { onChange, value } }) => (
                                <SelectInputM
                                    label="Durée"
                                    options={MATERIAL_DURATION_OPTIONS}
                                    value={value}
                                    onSelect={onChange}
                                />
                            )}
                        />
                    </View>
                </View>
            )}

            {/* Instructions */}
            {!hideTypeAndInstructions && (
            <View style={{ marginBottom: 15 }}>
                <View>
                    <View style={{ 
                        flexDirection: "row", 
                        alignItems: "center", 
                        justifyContent: "space-between",
                        marginBottom: 5 
                    }}>
                        <View style={{ flexDirection: "row", alignItems: "center", flexShrink: 1 }}>
                            <TextM
                                color={theme.colors.primary}
                                fontWeight="Medium"
                                fontSize={12}
                            >
                                Instructions
                            </TextM>
                            {instructionsHighlighted && (
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        marginLeft: 8,
                                        paddingVertical: 2,
                                        paddingHorizontal: 8,
                                        borderRadius: 999,
                                        backgroundColor: theme.colors.primaryLighter,
                                    }}
                                >
                                    <FontAwesome5
                                        name="magic"
                                        size={9}
                                        color={theme.colors.primary}
                                        style={{ marginRight: 5 }}
                                    />
                                    <TextM fontSize={10} fontWeight="Medium" color={theme.colors.primary}>
                                        Sam créera le chapitre
                                    </TextM>
                                </View>
                            )}
                        </View>
                        {studentInterests && (
                            <Tooltip title="Intègre son sport préféré dans les énoncés">
                                <TouchableOpacity
                                    onPress={() => setPersonalizeWithInterests(!personalizeWithInterests)}
                                    activeOpacity={0.7}
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        paddingVertical: 5,
                                        paddingHorizontal: 10,
                                        borderRadius: 14,
                                        backgroundColor: personalizeWithInterests
                                            ? theme.colors.primary
                                            : theme.colors.lighter,
                                        borderWidth: 1,
                                        borderColor: personalizeWithInterests
                                            ? theme.colors.primary
                                            : theme.colors.lightMore,
                                    }}
                                >
                                    <FontAwesome5
                                        name={personalizeWithInterests ? "futbol" : "magic"}
                                        size={11}
                                        color={personalizeWithInterests ? theme.colors.white : theme.colors.grey}
                                        style={{ marginRight: 6 }}
                                    />
                                    <TextM
                                        color={personalizeWithInterests ? theme.colors.white : theme.colors.grey}
                                        fontSize={11}
                                        fontWeight="Medium"
                                    >
                                        {personalizeWithInterests ? studentInterests : "Personnaliser"}
                                    </TextM>
                                    {personalizeWithInterests && (
                                        <FontAwesome5
                                            name="times"
                                            size={10}
                                            color={theme.colors.white}
                                            style={{ marginLeft: 6 }}
                                        />
                                    )}
                                </TouchableOpacity>
                            </Tooltip>
                        )}
                    </View>
                    <Controller
                        control={control}
                        name="instructions"
                        render={({ field: { onChange, value } }) => (
                            <UncontrolledTextInputM
                                ref={instructionsInputRef}
                                value={value}
                                onChangeText={(text) => {
                                    stopTypewriter()
                                    onChange(text)
                                }}
                                multiline
                                numberOfLines={2}
                                placeholder={
                                    isParentMode
                                        ? "Décrivez l'activité que vous souhaitez générer..."
                                        : "Décris l'activité que tu souhaites générer..."
                                }
                                mode="outlined"
                                outlineColor={theme.colors.lightMore}
                                activeOutlineColor={theme.colors.primary}
                                // outlineStyle, not outlineColor: UncontrolledTextInputM
                                // pins its own outline colours over the props it spreads.
                                outlineStyle={
                                    instructionsHighlighted
                                        ? { borderColor: theme.colors.primary, borderWidth: 2 }
                                        : undefined
                                }
                                style={{
                                    backgroundColor: theme.colors.white,
                                    minHeight: 100,
                                }}
                            />
                        )}
                    />
                    {!watchedChapter && !useStudyProgram && watchedInstructions?.length > 0 && watchedInstructions?.length < 5 && (
                        <TextM fontSize={11} color={theme.colors.grey} style={{ marginTop: 4 }}>
                            5 caractères minimum
                        </TextM>
                    )}
                </View>
            </View>
            )}

            {/* Primary action — overridden by the image flow with
                "Créer l'activité" (uses the uploaded image directly).
                Default: "Générer une activité" (AI-generated, text flow). */}
            {primaryActionOverride !== null ? (
                primaryActionOverride
            ) : (
                <TouchableRippleM
                    onPress={submit}
                    disabled={!isFormValid}
                    style={{
                        borderRadius: 15,
                        overflow: "hidden",
                        opacity: isFormValid ? 1 : 0.5,
                    }}
                >
                    <LinearGradient
                        colors={[theme.colors.samLight, theme.colors.purple]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={{
                            paddingVertical: 15,
                            paddingHorizontal: 30,
                            alignItems: "center",
                        }}
                    >
                        <TextM fontSize={16} fontWeight="Bold" color={theme.colors.white}>
                            Générer une activité
                        </TextM>
                    </LinearGradient>
                </TouchableRippleM>
            )}

            {/* Secondary actions (image flow only): separator + outlined
                "Pour réviser" / "Pour s'entraîner" buttons that generate
                similar activities at level 1 / 2 from the uploaded image. */}
            {secondaryActionsSlot}
        </>
    )
}

export default MaterialGenerationForm
